import { Box, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { StatCard, TabMenu, type TabItem } from '@themes/aegix';
import { SMSPageHeader as PageHeader } from '../components/layout/SMSPageHeader';
import {
  HiSquares2X2,
  HiChartBar,
  HiBolt,
  HiShieldCheck,
  HiArrowsPointingIn,
} from 'react-icons/hi2';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useActiveAlerts, useReunificationEvents } from '../hooks/queries';
import { DeliveryMetrics } from './DeliveryMetrics';
import { useHasPermission } from '@aegix/auth-permissions';

// Permission gating the delivery-metrics admin view. Reuses the
// existing notifications-admin permission so no new permission/role wiring
// is required; the same admins who manage device tokens see delivery health.
const DELIVERY_METRICS_PERMISSION = 'notifications.admin_fcm_device_token';

function ActiveEventsOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: alertsData, isLoading: alertsLoading } = useActiveAlerts({
    page_size: 1,
  });
  const { data: reunificationData, isLoading: reunificationLoading } =
    useReunificationEvents({
      status: 'active',
      page_size: 1,
    });
  const { data: drillsData, isLoading: drillsLoading } = useReunificationEvents(
    {
      status: 'active',
      is_drill: true,
      page_size: 1,
    },
  );

  const isLoading = alertsLoading || reunificationLoading || drillsLoading;

  if (isLoading) {
    return (
      <Flex justify="center" align="center" py={20}>
        <Spinner size="lg" color="brand.500" />
      </Flex>
    );
  }

  const activeAlerts = alertsData?.count ?? 0;
  const activeReunifications = reunificationData?.count ?? 0;
  const activeDrills = drillsData?.count ?? 0;

  return (
    <Box p="16px">
      <Text
        as="h2"
        fontSize="16px"
        fontWeight={600}
        color="text.primary"
        mb={4}
      >
        {t('dashboard.activeEvents', 'Active Events')}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="12px" mb={6}>
        <Box
          cursor="pointer"
          onClick={() => navigate('/events')}
          _hover={{ opacity: 0.8 }}
          transition="opacity 0.2s"
        >
          <StatCard
            label={t('dashboard.activeAlerts', 'Active Alerts')}
            value={activeAlerts}
            icon={HiShieldCheck}
            colorPalette="red"
            subtext={t('dashboard.viewAllAlerts', 'View all alerts')}
            data-e2e="active-alerts-stat"
          />
        </Box>
        <Box
          cursor="pointer"
          onClick={() => navigate('/events')}
          _hover={{ opacity: 0.8 }}
          transition="opacity 0.2s"
        >
          <StatCard
            label={t('dashboard.activeDrills', 'Active Drills')}
            value={activeDrills}
            icon={HiBolt}
            colorPalette="orange"
            subtext={t('dashboard.viewAllDrills', 'View all drills')}
            data-e2e="active-drills-stat"
          />
        </Box>
        <Box
          cursor="pointer"
          onClick={() => navigate('/reunification')}
          _hover={{ opacity: 0.8 }}
          transition="opacity 0.2s"
        >
          <StatCard
            label={t('dashboard.activeReunifications', 'Active Reunifications')}
            value={activeReunifications}
            icon={HiArrowsPointingIn}
            colorPalette="blue"
            subtext={t(
              'dashboard.viewReunificationEvents',
              'View reunification events',
            )}
            data-e2e="active-reunifications-stat"
          />
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export function Dashboard() {
  const { t } = useTranslation();
  usePageTitle(t('dashboard.title', 'Dashboard'));
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewDeliveryMetrics = useHasPermission(DELIVERY_METRICS_PERMISSION);

  const tabs: TabItem[] = [
    {
      value: 'overview',
      label: t('dashboard.tab.overview', 'Overview'),
      icon: HiSquares2X2,
      'data-e2e': 'tab-overview',
    },
    ...(canViewDeliveryMetrics
      ? ([
          {
            value: 'delivery',
            label: t('dashboard.tab.delivery', 'Delivery Metrics'),
            icon: HiChartBar,
            'data-e2e': 'tab-delivery-metrics',
          },
        ] as TabItem[])
      : []),
  ];

  const requestedTab = searchParams.get('tab') || 'overview';
  const activeTab =
    requestedTab === 'delivery' && !canViewDeliveryMetrics
      ? 'overview'
      : requestedTab;

  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  return (
    <Box mx={-8} mt={-8}>
      <PageHeader
        icon={HiSquares2X2}
        title={t('dashboard.title', 'Dashboard')}
        subtitle={t(
          'dashboard.subtitle',
          'Safety Management System overview and metrics',
        )}
        showSearch={true}
        showNotifications={true}
      />
      <TabMenu
        tabs={tabs}
        activeTab={activeTab}
        onChange={handleTabChange}
        e2eTitle="DashboardTabs"
      />

      {activeTab === 'overview' && <ActiveEventsOverview />}
      {activeTab === 'delivery' && canViewDeliveryMetrics && (
        <DeliveryMetrics embedded />
      )}
    </Box>
  );
}
